import React, { useState, useEffect } from 'react';
import AnalyticsQueries from '../../../../requests/queries/AnalyticsQueries';
import LoadingSpinner from '../../../common/Loading';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

// Composant Analytics
const Analytics = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [paymentsByDay, setPaymentsByDay] = useState(null);
    const [paymentMethodsData, setPaymentMethodsData] = useState(null);
    const [amountDistributionData, setAmountDistributionData] = useState(null);
    const [revenueByHourData, setRevenueByHourData] = useState(null);
    const [periodComparison, setPeriodComparison] = useState(null);
    const [periodFilter, setPeriodFilter] = useState(30);
  
    useEffect(() => {
      loadAnalyticsData();
    }, [periodFilter]);
  
    const loadAnalyticsData = async () => {
      try {
        setIsLoading(true);
        const [
          statsRes, 
          revenueRes, 
          paymentsRes, 
          methodsRes, 
          amountRes, 
          hourRes, 
          comparisonRes
        ] = await Promise.all([
          AnalyticsQueries.getOverallStats(periodFilter),
          AnalyticsQueries.getRevenueByPeriod('month', 12),
          AnalyticsQueries.getPaymentsByDay(7),
          AnalyticsQueries.getPaymentMethodsDistribution(periodFilter),
          AnalyticsQueries.getAmountDistribution(periodFilter),
          AnalyticsQueries.getRevenueByHour(periodFilter),
          AnalyticsQueries.getPeriodComparison(periodFilter),
        ]);
  
        setStats(statsRes.data.stats);
        setPeriodComparison(comparisonRes.data.data);
        
        // Préparer les données de revenus pour le graphique
        if (revenueRes.data.data) {
          setRevenueData({
            labels: revenueRes.data.data.map(d => d.period),
            datasets: [
              {
                label: 'Revenus (€)',
                data: revenueRes.data.data.map(d => d.revenue),
                borderColor: '#000',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                tension: 0.4,
                fill: true,
              },
            ],
          });
        }
  
        // Préparer les données de paiements par jour
        if (paymentsRes.data.data) {
          const orderedDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
          setPaymentsByDay({
            labels: orderedDays,
            datasets: [
              {
                label: 'Commandes',
                data: orderedDays.map(day => {
                  const dayData = paymentsRes.data.data.find(d => d.day === day);
                  return dayData ? dayData.count : 0;
                }),
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderColor: '#000',
                borderWidth: 1,
              },
            ],
          });
        }
  
        // Préparer les données de méthodes de paiement
        if (methodsRes.data.data && methodsRes.data.data.length > 0) {
          const methodNames = {
            'card': 'Carte bancaire',
            'bancontact': 'Bancontact',
            'klarna': 'Klarna',
            'paypal': 'PayPal',
            'unknown': 'Autre'
          };
          setPaymentMethodsData({
            labels: methodsRes.data.data.map(d => methodNames[d.method] || d.method),
            datasets: [
              {
                data: methodsRes.data.data.map(d => d.revenue),
                backgroundColor: ['#000', '#333', '#666', '#999', '#ccc'],
                borderWidth: 0,
              },
            ],
          });
        }
  
        // Préparer les données de distribution des montants
        if (amountRes.data.data) {
          setAmountDistributionData({
            labels: amountRes.data.data.map(d => d.range),
            datasets: [
              {
                label: 'Nombre de commandes',
                data: amountRes.data.data.map(d => d.count),
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderColor: '#000',
                borderWidth: 1,
              },
            ],
          });
        }
  
        // Préparer les données de revenus par heure
        if (hourRes.data.data) {
          setRevenueByHourData({
            labels: hourRes.data.data.map(d => `${d.hour}h`),
            datasets: [
              {
                label: 'Revenus (€)',
                data: hourRes.data.data.map(d => d.revenue),
                borderColor: '#000',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                tension: 0.4,
                fill: true,
              },
            ],
          });
        }
  
        setIsLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des analytics:', err);
        setError(err.response?.data?.message || 'Une erreur est survenue');
        setIsLoading(false);
      }
    };
  
    if (isLoading) {
      return <LoadingSpinner />;
    }
  
    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }
  
    // Données pour les graphiques (avec données réelles ou par défaut)
    const salesData = revenueData || {
      labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
      datasets: [
        {
          label: 'Revenus (€)',
          data: [0, 0, 0, 0, 0, 0],
          borderColor: '#000',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          tension: 0.4,
          fill: true,
        },
      ],
    };
  
    const ordersData = paymentsByDay || {
      labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
      datasets: [
        {
          label: 'Commandes',
          data: [0, 0, 0, 0, 0, 0, 0],
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#000',
          borderWidth: 1,
        },
      ],
    };
  
    const categoryData = {
      labels: ['T-shirts', 'Hoodies', 'Pantalons', 'Shorts', 'Vestes', 'Accessoires'],
      datasets: [
        {
          data: [35, 25, 20, 10, 7, 3],
          backgroundColor: [
            '#000',
            '#333',
            '#666',
            '#999',
            '#ccc',
            '#f0f0f0',
          ],
          borderWidth: 0,
        },
      ],
    };
  
    const topProducts = [
      { name: 'T-shirt Street Style', sales: 156, revenue: '€4,680' },
      { name: 'Hoodie Urban', sales: 89, revenue: '€7,120' },
      { name: 'Pantalon Cargo', sales: 67, revenue: '€6,030' },
      { name: 'Short Sport', sales: 45, revenue: '€2,250' },
      { name: 'Veste Denim', sales: 34, revenue: '€3,400' },
    ];
  
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: '#000',
            font: {
              family: 'Inter',
              size: 12,
              weight: '600',
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
          ticks: {
            color: '#666',
            font: {
              family: 'Inter',
              size: 11,
            },
          },
        },
        y: {
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
          ticks: {
            color: '#666',
            font: {
              family: 'Inter',
              size: 11,
            },
          },
        },
      },
    };
  
    return (
      <div className="analytics-section">
        <div className="section-header">
          <h2>Analytics & Performance</h2>
          <div className="analytics-filters">
            <select 
              className="form-control" 
              value={periodFilter}
              onChange={(e) => setPeriodFilter(parseInt(e.target.value))}
            >
              <option value={7}>Derniers 7 jours</option>
              <option value={30}>Derniers 30 jours</option>
              <option value={90}>Derniers 3 mois</option>
              <option value={365}>Cette année</option>
            </select>
          </div>
        </div>
  
        {/* Métriques clés avec comparaison */}
        <div className="analytics-metrics">
          <div className="metric-card">
            <div className="metric-icon">
              <i className="bi bi-graph-up"></i>
            </div>
            <div className="metric-content">
              <h3>CA Total</h3>
              <p className="metric-value">
                €{stats ? stats.totalRevenue.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
              </p>
              {periodComparison && (
                <p className={`metric-change ${periodComparison.revenueChange >= 0 ? 'positive' : 'negative'}`}>
                  {periodComparison.revenueChange >= 0 ? '+' : ''}{periodComparison.revenueChange.toFixed(1)}% vs période précédente
                </p>
              )}
              {!periodComparison && <p className="metric-change positive">Sur {periodFilter} jours</p>}
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-icon">
              <i className="bi bi-cart-check"></i>
            </div>
            <div className="metric-content">
              <h3>Commandes</h3>
              <p className="metric-value">{stats ? stats.orderCount : 0}</p>
              {periodComparison && (
                <p className={`metric-change ${periodComparison.countChange >= 0 ? 'positive' : 'negative'}`}>
                  {periodComparison.countChange >= 0 ? '+' : ''}{periodComparison.countChange.toFixed(1)}% vs période précédente
                </p>
              )}
              {!periodComparison && <p className="metric-change positive">Commandes réussies</p>}
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-icon">
              <i className="bi bi-people"></i>
            </div>
            <div className="metric-content">
              <h3>Nouveaux Clients</h3>
              <p className="metric-value">{stats ? stats.newCustomers : 0}</p>
              <p className="metric-change positive">Clients uniques</p>
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-icon">
              <i className="bi bi-currency-euro"></i>
            </div>
            <div className="metric-content">
              <h3>Panier Moyen</h3>
              <p className="metric-value">
                €{stats ? stats.averageOrderValue.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
              </p>
              <p className="metric-change positive">Par commande</p>
            </div>
          </div>
        </div>
  
        {/* Graphiques principaux */}
        <div className="charts-grid">
          <div className="chart-container">
            <h3>Évolution des Ventes (12 derniers mois)</h3>
            <div className="chart-wrapper">
              <Line data={salesData} options={chartOptions} />
            </div>
          </div>
          
          <div className="chart-container">
            <h3>Commandes par Jour de la Semaine</h3>
            <div className="chart-wrapper">
              <Bar data={ordersData} options={chartOptions} />
            </div>
          </div>
        </div>
  
        {/* Graphiques secondaires */}
        <div className="charts-grid" style={{ marginTop: '30px' }}>
          {paymentMethodsData && (
            <div className="chart-container">
              <h3>Répartition par Méthode de Paiement</h3>
              <div className="chart-wrapper doughnut">
                <Doughnut data={paymentMethodsData} options={chartOptions} />
              </div>
            </div>
          )}
          
          {amountDistributionData && (
            <div className="chart-container">
              <h3>Distribution des Montants de Commandes</h3>
              <div className="chart-wrapper">
                <Bar 
                  data={amountDistributionData} 
                  options={{
                    ...chartOptions,
                    indexAxis: 'y',
                  }} 
                />
              </div>
            </div>
          )}
        </div>
  
        {/* Graphique revenus par heure */}
        {revenueByHourData && (
          <div className="charts-grid" style={{ marginTop: '30px' }}>
            <div className="chart-container" style={{ gridColumn: '1 / -1' }}>
              <h3>Revenus par Heure de la Journée</h3>
              <div className="chart-wrapper">
                <Bar 
                  data={revenueByHourData} 
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }} 
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
export default Analytics;